import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';

import {
  CoreJsonObject,
  CoreProviderFieldDescriptor,
  CoreProviderFieldOption,
} from '@core/api/core';
import { ProviderFilterFieldsComponent } from '@shared/components/provider-filter-fields/provider-filter-fields.component';
import {
  isProviderFieldValueActive,
  ProviderFieldValueChange,
} from '@shared/components/provider-filter-fields/provider-filter-fields.utils';
import { VeevaService } from '@shared/services/integrations/veeva.service';

@Component({
  selector: 'app-veeva-dynamic-filters',
  templateUrl: './veeva-dynamic-filters.component.html',
  styleUrl: './veeva-dynamic-filters.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatProgressSpinner, ProviderFilterFieldsComponent, RouterLink],
})
export class VeevaDynamicFiltersComponent implements OnInit {
  readonly disabled = input(false);
  readonly filtersChanged = output<CoreJsonObject>();
  readonly loadingChange = output<boolean>();

  private readonly _veevaService = inject(VeevaService);
  private readonly _destroyRef = inject(DestroyRef);

  protected readonly fields = signal<CoreProviderFieldDescriptor[]>([]);
  protected readonly loading = signal(false);
  protected readonly loadFailed = signal(false);
  protected readonly selected = signal<Record<string, unknown>>({});
  protected readonly optionMap = signal<Record<string, CoreProviderFieldOption[]>>({});
  protected readonly optionLoading = signal<Record<string, boolean>>({});
  protected readonly optionLoadFailed = signal<Record<string, boolean>>({});

  ngOnInit(): void {
    this.loadFields();
  }

  loadFields(): void {
    this.loading.set(true);
    this.loadFailed.set(false);
    this.loadingChange.emit(true);

    this._veevaService
      .getImportFields()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: fields => {
          this.fields.set(fields);
          this.selected.set({});
          this.filtersChanged.emit({});
        },
        error: () => {
          this.fields.set([]);
          this.loadFailed.set(true);
          this.loading.set(false);
          this.loadingChange.emit(false);
        },
        complete: () => {
          this.loading.set(false);
          this.loadingChange.emit(false);
        },
      });
  }

  protected setFieldValue(change: ProviderFieldValueChange): void {
    const next = { ...this.selected() };
    if (isProviderFieldValueActive(change.field, change.value)) {
      next[change.field.name] = change.value;
    } else {
      delete next[change.field.name];
    }
    this.selected.set(next);
    this.filtersChanged.emit(next);
  }

  protected loadOptions(field: CoreProviderFieldDescriptor): void {
    if (
      field.valuesSource !== 'dynamic' ||
      this.optionMap()[field.name] ||
      this.optionLoading()[field.name]
    ) {
      return;
    }

    this.optionLoading.update(state => ({ ...state, [field.name]: true }));
    this.optionLoadFailed.update(state => ({ ...state, [field.name]: false }));
    this._veevaService
      .getImportFieldValues(field.name)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: options => {
          this.optionMap.update(state => ({ ...state, [field.name]: options }));
        },
        error: () => {
          this.optionMap.update(state => ({ ...state, [field.name]: [] }));
          this.optionLoading.update(state => ({ ...state, [field.name]: false }));
          this.optionLoadFailed.update(state => ({ ...state, [field.name]: true }));
        },
        complete: () => {
          this.optionLoading.update(state => ({ ...state, [field.name]: false }));
        },
      });
  }

  protected retryOptions(field: CoreProviderFieldDescriptor): void {
    this._veevaService.clearFiltersCache();
    this.optionMap.update(state => {
      const next = { ...state };
      delete next[field.name];
      return next;
    });
    this.loadOptions(field);
  }
}
